import { useState } from 'react';
import { useGameStore } from '@hollowveil/shared';
import { useSoundStore } from '../../hooks/useSounds';
import './GameToolbar.css';

const VISUAL_EFFECTS = [
  { id: null, name: 'Aucun', description: 'Désactiver les effets' },
  { id: 'blur', name: 'Flou', description: 'Vision trouble' },
  { id: 'vignette', name: 'Vignette', description: 'Bords assombris' },
  { id: 'crt', name: 'CRT', description: 'Effet écran cathodique' },
  { id: 'glitch', name: 'Glitch', description: 'Perturbations visuelles' },
  { id: 'pixelate', name: 'Pixelisé', description: 'Style rétro' },
  { id: 'dreamlike', name: 'Onirique', description: 'Atmosphère de rêve' },
  { id: 'underwater', name: 'Sous-marin', description: 'Effet aquatique' },
  { id: 'nightvision', name: 'Vision nocturne', description: 'Teinte verte' },
  { id: 'sepia', name: 'Sépia', description: 'Style ancien' },
  { id: 'inverted', name: 'Inversé', description: 'Couleurs inversées' },
  { id: 'saturated', name: 'Saturé', description: 'Couleurs vives' },
  { id: 'desaturated', name: 'Désaturé', description: 'Presque noir et blanc' },
  { id: 'cold', name: 'Froid', description: 'Teintes bleues' },
  { id: 'warm', name: 'Chaud', description: 'Teintes orangées' },
];

function getItemIcon(iconId: string): string {
  switch (iconId) {
    case 'crystal': return '💎';
    case 'key': return '🔑';
    case 'scroll': return '📜';
    case 'potion': return '🧪';
    case 'gem': return '💠';
    case 'flower': return '🌸';
    case 'orb': return '🔮';
    case 'coin': return '🪙';
    case 'book': return '📕';
    case 'feather': return '🪶';
    case 'star': return '⭐';
    default: return '📦';
  }
}

type PanelType = 'none' | 'game' | 'dev';
type SubPanelType = 'quests' | 'inventory' | 'levels' | 'effects' | 'help';

export function GameToolbar() {
  const {
    player,
    rooms,
    completedRooms,
    currentRoom,
    currentQuest,
    debugMode,
    toggleDebugMode,
    goToRoom,
    visualEffect,
    setVisualEffect,
    config,
    resetInventory,
    showJoystick,
    showZoomControls,
    toggleJoystick,
    toggleZoomControls,
    inputMode,
  } = useGameStore();

  const [activePanel, setActivePanel] = useState<PanelType>('none');
  const [activeSubPanel, setActiveSubPanel] = useState<SubPanelType>('quests');

  // Sound settings
  const { musicEnabled, soundEffectsEnabled, setMusicEnabled, setSoundEffectsEnabled } = useSoundStore();

  const inventory = player.inventory;
  const roomIds = Array.from(rooms.keys());
  const lastCompletedIndex = Math.max(
    ...completedRooms.map((id) => roomIds.indexOf(id)),
    -1
  );

  const togglePanel = (panel: PanelType) => {
    if (activePanel === panel) {
      setActivePanel('none');
    } else {
      setActivePanel(panel);
      // Set default sub-panel
      if (panel === 'game') {
        setActiveSubPanel('quests');
      } else if (panel === 'dev') {
        setActiveSubPanel('levels');
      }
    }
  };

  const handleRoomSelect = (roomId: string) => {
    goToRoom(roomId);
    setActivePanel('none');
  };

  const handleEffectSelect = (effectId: string | null) => {
    setVisualEffect(effectId);
  };

  // Count incomplete objectives
  const incompleteObjectives = currentQuest?.objectives.filter(o => !o.completed).length || 0;

  return (
    <div className="game-toolbar">
      {/* Main toolbar buttons */}
      <div className="toolbar-buttons">
        {/* Game button (Quests + Inventory) */}
        <button
          className={`toolbar-btn game-btn ${activePanel === 'game' ? 'active' : ''}`}
          onClick={() => togglePanel('game')}
          title="Jeu"
        >
          <span className="btn-icon">🎮</span>
          <span className="btn-label">Jeu</span>
          {(incompleteObjectives > 0 || inventory.length > 0) && (
            <span className="badge">{incompleteObjectives + inventory.length}</span>
          )}
        </button>

        {/* Dev/Debug button (Levels + Effects + Debug) */}
        <button
          className={`toolbar-btn dev-btn ${activePanel === 'dev' ? 'active' : ''} ${debugMode ? 'debug-active' : ''}`}
          onClick={() => togglePanel('dev')}
          title="Options"
        >
          <span className="btn-icon">⚙️</span>
          <span className="btn-label">Options</span>
        </button>
      </div>

      {/* Game Panel */}
      {activePanel === 'game' && (
        <div className="toolbar-panel">
          <div className="panel-header">
            <div className="panel-tabs">
              <button
                className={`panel-tab ${activeSubPanel === 'quests' ? 'active' : ''}`}
                onClick={() => setActiveSubPanel('quests')}
              >
                <span className="tab-icon">📜</span>
                <span className="tab-text">Quêtes</span>
                {incompleteObjectives > 0 && <span className="tab-badge">{incompleteObjectives}</span>}
              </button>
              <button
                className={`panel-tab ${activeSubPanel === 'inventory' ? 'active' : ''}`}
                onClick={() => setActiveSubPanel('inventory')}
              >
                <span className="tab-icon">🎒</span>
                <span className="tab-text">Sac</span>
                {inventory.length > 0 && <span className="tab-badge">{inventory.length}</span>}
              </button>
              <button
                className={`panel-tab ${activeSubPanel === 'levels' ? 'active' : ''}`}
                onClick={() => setActiveSubPanel('levels')}
              >
                <span className="tab-icon">📋</span>
                <span className="tab-text">Niveaux</span>
              </button>
              <button
                className={`panel-tab ${activeSubPanel === 'help' ? 'active' : ''}`}
                onClick={() => setActiveSubPanel('help')}
              >
                <span className="tab-icon">❓</span>
                <span className="tab-text">Aide</span>
              </button>
            </div>
            <button className="panel-close" onClick={() => setActivePanel('none')}>×</button>
          </div>

          <div className="panel-content">
            {/* Quests Sub-Panel */}
            {activeSubPanel === 'quests' && (
              <div className="quests-panel">
                {currentQuest ? (
                  <>
                    <div className="quest-title">{currentQuest.name}</div>
                    <div className="quest-description">{currentQuest.description}</div>
                    <div className="objectives-list">
                      {currentQuest.objectives.map((obj) => (
                        <div
                          key={obj.id}
                          className={`objective-item ${obj.completed ? 'completed' : ''}`}
                        >
                          <span className="objective-check">
                            {obj.completed ? '✓' : obj.requiredWorld === 'spectral' ? '👁' : '○'}
                          </span>
                          <span className="objective-text">{obj.description}</span>
                          {obj.requiredWorld && (
                            <span className="objective-world">
                              {obj.requiredWorld === 'real' ? '☀️' : '🌙'}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="empty-state">Aucune quête active</div>
                )}
              </div>
            )}

            {/* Inventory Sub-Panel */}
            {activeSubPanel === 'inventory' && (
              <div className="inventory-list">
                {inventory.length === 0 ? (
                  <div className="inventory-empty">
                    <p>Ton inventaire est vide.</p>
                    <p className="hint">Explore le monde pour trouver des objets !</p>
                  </div>
                ) : (
                  inventory.map((item, index) => (
                    <div key={`${item.id}-${index}`} className="inventory-item">
                      <span className="item-icon">{getItemIcon(item.iconId)}</span>
                      <div className="item-details">
                        <span className="item-name">{item.name}</span>
                        <span className="item-desc">{item.description}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Levels Sub-Panel (in Game panel) */}
            {activeSubPanel === 'levels' && (
              <div className="levels-list">
                {roomIds.map((roomId, index) => {
                  const room = rooms.get(roomId);
                  if (!room) return null;

                  const isCompleted = completedRooms.includes(roomId);
                  const isCurrent = currentRoom?.id === roomId;
                  const isNaturallyUnlocked = index === 0 || index <= lastCompletedIndex + 1;
                  const isUnlocked = debugMode || isNaturallyUnlocked;
                  const quest = config?.quests?.find((q) => q.roomId === roomId);

                  return (
                    <button
                      key={roomId}
                      className={`level-btn ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''} ${!isUnlocked ? 'locked' : ''} ${debugMode && !isNaturallyUnlocked ? 'debug-unlocked' : ''}`}
                      onClick={() => isUnlocked && handleRoomSelect(roomId)}
                      disabled={!isUnlocked}
                    >
                      <span className="level-num">{index + 1}</span>
                      <div className="level-info">
                        <span className="level-name">{room.name}</span>
                        {quest && <span className="level-quest">{quest.name}</span>}
                      </div>
                      <span className="level-status">
                        {isCompleted && '✓'}
                        {isCurrent && !isCompleted && '●'}
                        {!isUnlocked && !debugMode && '🔒'}
                        {debugMode && !isNaturallyUnlocked && !isCompleted && '🔧'}
                      </span>
                    </button>
                  );
                })}
                <div className="levels-progress">
                  Progression: {completedRooms.length} / {roomIds.length} niveaux
                </div>
              </div>
            )}

            {/* Help Sub-Panel */}
            {activeSubPanel === 'help' && (
              <div className="help-panel">
                {/* Interface toggles */}
                <div className="help-section">
                  <div className="help-section-title">Interface</div>
                  <div className="help-toggles">
                    <button
                      className={`help-toggle-btn ${showJoystick ? 'active' : ''}`}
                      onClick={toggleJoystick}
                    >
                      <span className="toggle-icon">🕹️</span>
                      <span className="toggle-label">Joystick</span>
                      <span className="toggle-status">{showJoystick ? 'ON' : 'OFF'}</span>
                    </button>
                    <button
                      className={`help-toggle-btn ${showZoomControls ? 'active' : ''}`}
                      onClick={toggleZoomControls}
                    >
                      <span className="toggle-icon">🔍</span>
                      <span className="toggle-label">Zoom</span>
                      <span className="toggle-status">{showZoomControls ? 'ON' : 'OFF'}</span>
                    </button>
                  </div>
                </div>

                {/* Sound settings */}
                <div className="help-section">
                  <div className="help-section-title">Son</div>
                  <div className="help-toggles">
                    <button
                      className={`help-toggle-btn ${musicEnabled ? 'active' : ''}`}
                      onClick={() => setMusicEnabled(!musicEnabled)}
                    >
                      <span className="toggle-icon">🎵</span>
                      <span className="toggle-label">Musique</span>
                      <span className="toggle-status">{musicEnabled ? 'ON' : 'OFF'}</span>
                    </button>
                    <button
                      className={`help-toggle-btn ${soundEffectsEnabled ? 'active' : ''}`}
                      onClick={() => setSoundEffectsEnabled(!soundEffectsEnabled)}
                    >
                      <span className="toggle-icon">🔊</span>
                      <span className="toggle-label">Effets</span>
                      <span className="toggle-status">{soundEffectsEnabled ? 'ON' : 'OFF'}</span>
                    </button>
                  </div>
                </div>

                {/* Controls explanation */}
                <div className="help-section">
                  <div className="help-section-title">Contrôles</div>
                  <div className="help-controls">
                    {inputMode === 'touch' ? (
                      <>
                        <div className="help-control">
                          <span className="control-icon">👆</span>
                          <div className="control-info">
                            <span className="control-name">Toucher l'écran</span>
                            <span className="control-desc">Déplacer le personnage</span>
                          </div>
                        </div>
                        <div className="help-control">
                          <span className="control-icon">👆</span>
                          <div className="control-info">
                            <span className="control-name">Maintenir appuyé</span>
                            <span className="control-desc">Suivre le doigt en continu</span>
                          </div>
                        </div>
                        <div className="help-control">
                          <span className="control-icon">🕹️</span>
                          <div className="control-info">
                            <span className="control-name">Joystick</span>
                            <span className="control-desc">Direction précise</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="help-control">
                          <span className="control-icon">⌨️</span>
                          <div className="control-info">
                            <span className="control-name">ZQSD / Flèches</span>
                            <span className="control-desc">Déplacer le personnage</span>
                          </div>
                        </div>
                        <div className="help-control">
                          <span className="control-icon">🖱️</span>
                          <div className="control-info">
                            <span className="control-name">Clic gauche</span>
                            <span className="control-desc">Se déplacer vers ce point</span>
                          </div>
                        </div>
                        <div className="help-control">
                          <span className="control-icon">🖱️</span>
                          <div className="control-info">
                            <span className="control-name">Maintenir + glisser</span>
                            <span className="control-desc">Suivre la souris</span>
                          </div>
                        </div>
                      </>
                    )}
                    <div className="help-control">
                      <span className="control-icon">🌙</span>
                      <div className="control-info">
                        <span className="control-name">Espace / Bouton monde</span>
                        <span className="control-desc">Changer de dimension</span>
                      </div>
                    </div>
                    <div className="help-control">
                      <span className="control-icon">💬</span>
                      <div className="control-info">
                        <span className="control-name">Approcher un objet</span>
                        <span className="control-desc">Interagir automatiquement</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tips */}
                <div className="help-section">
                  <div className="help-section-title">Astuces</div>
                  <div className="help-tips">
                    <p>☀️ Certains objets n'existent que dans le monde réel</p>
                    <p>🌙 D'autres n'apparaissent que dans le monde spectral</p>
                    <p>👁️ Observe bien les deux mondes pour résoudre les énigmes</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dev Panel */}
      {activePanel === 'dev' && (
        <div className="toolbar-panel dev-panel">
          <div className="panel-header">
            <div className="panel-tabs">
              <button
                className={`panel-tab ${activeSubPanel === 'levels' ? 'active' : ''}`}
                onClick={() => setActiveSubPanel('levels')}
              >
                <span className="tab-icon">📋</span>
                <span className="tab-text">Niveaux</span>
              </button>
              <button
                className={`panel-tab ${activeSubPanel === 'effects' ? 'active' : ''}`}
                onClick={() => setActiveSubPanel('effects')}
              >
                <span className="tab-icon">✨</span>
                <span className="tab-text">Effets</span>
              </button>
            </div>
            <button className="panel-close" onClick={() => setActivePanel('none')}>×</button>
          </div>

          <div className="panel-content">
            {/* Debug Toggle */}
            <div className="debug-toggle-section">
              <button
                className={`debug-toggle-btn ${debugMode ? 'active' : ''}`}
                onClick={toggleDebugMode}
              >
                <span>🔧</span>
                <span>Mode Debug: {debugMode ? 'ON' : 'OFF'}</span>
              </button>
            </div>

            {/* Levels Sub-Panel */}
            {activeSubPanel === 'levels' && (
              <div className="levels-list">
                {roomIds.map((roomId, index) => {
                  const room = rooms.get(roomId);
                  if (!room) return null;

                  const isCompleted = completedRooms.includes(roomId);
                  const isCurrent = currentRoom?.id === roomId;
                  const isNaturallyUnlocked = index === 0 || index <= lastCompletedIndex + 1;
                  const isUnlocked = debugMode || isNaturallyUnlocked;
                  const quest = config?.quests?.find((q) => q.roomId === roomId);

                  return (
                    <button
                      key={roomId}
                      className={`level-btn ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''} ${!isUnlocked ? 'locked' : ''} ${debugMode && !isNaturallyUnlocked ? 'debug-unlocked' : ''}`}
                      onClick={() => isUnlocked && handleRoomSelect(roomId)}
                      disabled={!isUnlocked}
                    >
                      <span className="level-num">{index + 1}</span>
                      <div className="level-info">
                        <span className="level-name">{room.name}</span>
                        {quest && <span className="level-quest">{quest.name}</span>}
                      </div>
                      <span className="level-status">
                        {isCompleted && '✓'}
                        {isCurrent && !isCompleted && '●'}
                        {!isUnlocked && !debugMode && '🔒'}
                        {debugMode && !isNaturallyUnlocked && !isCompleted && '🔧'}
                      </span>
                    </button>
                  );
                })}
                <div className="levels-progress">
                  Progression: {completedRooms.length} / {roomIds.length} niveaux
                </div>
              </div>
            )}

            {/* Effects Sub-Panel */}
            {activeSubPanel === 'effects' && (
              <div className="effects-list">
                {VISUAL_EFFECTS.map((effect) => (
                  <button
                    key={effect.id || 'none'}
                    className={`effect-btn ${visualEffect === effect.id ? 'active' : ''}`}
                    onClick={() => handleEffectSelect(effect.id)}
                  >
                    <span className="effect-name">{effect.name}</span>
                    <span className="effect-desc">{effect.description}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Debug Reset Inventory */}
            {debugMode && inventory.length > 0 && (
              <button
                className="debug-reset-btn"
                onClick={() => resetInventory()}
              >
                🗑️ Reset Inventaire
              </button>
            )}
          </div>
        </div>
      )}

      {/* Backdrop */}
      {activePanel !== 'none' && (
        <div className="toolbar-backdrop" onClick={() => setActivePanel('none')} />
      )}
    </div>
  );
}
